import { EnterHook } from 'react-router'

import {
  BROWSER_ERROR_KEYS,
  BrowserErrors,
  instrumentBrowserError,
} from '@library/observability/browser'

/**
 * Type-guard to ensure that the provided Object fulfills the EMAC requirement
 * to be considered `thenable` (Promise-like) by the runtime. This only requires
 * the value to be an `object` with a `then` function.
 *
 * @param val The value to be checked
 * @returns Where the provided value is `thenable`
 */
function isThenable<T = unknown>(val: unknown): val is PromiseLike<T> {
  return (
    !!val &&
    (typeof val === 'object' || typeof val === 'function') &&
    typeof (val as { then?: unknown }).then === 'function'
  )
}

/**
 * Instrument `react-router` `onEnter` methods, so that errors are sent to the
 * monitoring platform.
 *
 * This method will ensure that an error will only be sent once to the monitoring
 * platform, even if the underlying `onEnter` method either calls `next()` multiple
 * times, or calls `next()` and returns a Promise-like entity.
 *
 * @param enterHook The method to be instrumented
 * @param errorKey The error key to log an error against
 * @param details Extra values to provide to the monitoring platform
 * @returns A wrapped version of the provided `enterHook`
 */
export const instrumentedOnEnter =
  <ErrorKey extends BROWSER_ERROR_KEYS>(
    enterHook: EnterHook,
    errorKey: ErrorKey,
    details: typeof BrowserErrors[ErrorKey]['detail'],
  ): EnterHook =>
  (next, replace, callback) => {
    let settled = false
    const done = (err?: Error) => {
      if (settled) return
      settled = true
      if (err) instrumentBrowserError(errorKey, details, err)

      if (!callback) return
      if (err) {
        callback(err)
      } else {
        callback()
      }
    }

    try {
      // If the hook *declares* it uses the callback, pass it through
      if (enterHook.length >= 3) {
        const wrappedCb = (err?: Error) => done(err)
        enterHook(next, replace, wrappedCb)

        return
      }

      // Callback-less hook: support sync or promise styles
      const ret = enterHook(next, replace)

      if (isThenable(ret)) {
        ret.then(() => done(), done) // no .catch needed
      } else {
        done() // sync success
      }
    } catch (err: unknown) {
      done(err as Error)
    }
  }
